import {CellBuilderFlagIcon} from "/@back@/commons/widgets/grid-libs.js";
import {GridColDef, IGrid, IGridDataRow} from "/@back@/commons/widgets/grid-core.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {JGitSrcFields, WSP_GIT} from "/@lib@/wsp/git.js";
import {DiffBarPanelAction, IDiffBarPanel, IWedDiffBar, JWedDiffBarParams} from "/@back@/edit/wed/features/diffBar.js";
import {NavigatorArea} from "/@back@/wsp/areas/wspWorkbench.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {LANG} from "/@lib@/commons/lang.js";
import {Navigator, NavigatorFacetText} from "/@back@/wsp/views/navigator.js";


class CellBuilderGitFlag extends CellBuilderFlagIcon {

	iconWidth = ".8rem";

	iconFilter = "var(--filter)";

	protected _buildContent(row: IGridDataRow, root: HTMLElement): HTMLElement {
		DOM.addClass(root, 'center');
		return super._buildContent(row, root);
	}

	protected _getIcon(row: IGridDataRow): string {
		const st = WSP_GIT.getSimpleState(super._getValue(row));
		return st !== "insynch" && st !== "none" ? "--git-" + st + "-url" : "";
	}

	protected _getValue(row: IGridDataRow): any {
		return WSP_GIT.getGitStateLabel(WSP_GIT.getSimpleState(super._getValue(row)));
	}
}

REG.reg.registerSkin("wsp-explorer/grid/git", 1, /* language=CSS */ `
	:host {
		--git-untracked-url: url(/@skin@/wsp/objects/gitState/untracked.svg);
		--git-ignored-url: url(/@skin@/wsp/objects/gitState/ignored.svg);
		--git-adding-url: url(/@skin@/wsp/objects/gitState/adding.svg);
		--git-updating-url: url(/@skin@/wsp/objects/gitState/updating.svg);
		--git-deleting-url: url(/@skin@/wsp/objects/gitState/deleting.svg);
		--git-conflict-url: url(/@skin@/wsp/objects/gitState/conflict.svg);
	}

	.colW.git {
		border: none;
	}

`);

/**
 * Init du registre spécifique à un atelier pouvant gérer une couche Git.
 */
export function initGitReg(reg: IReg<IWspUiEnv>) {

	const gitColDef = new GridColDef("git")
		.setFlex("1rem", 0, 0)
		.setCellBuilder(new CellBuilderGitFlag("gitSt"))
		.setSkin("wsp-explorer/grid/git")
		.setLabel("")
		.setDescription("Statut Git");

	/** Désactivation colonne Git à l'init => rechargement widget nécessaire si détection Git ultérieure. */
	gitColDef.isAvailable = function (grid: IGrid): boolean {
		return (reg.env.wsp.infoWsp?.srcFields as JGitSrcFields)?.gitSt != null;
	}

	reg.addToList("columns:wspExplorer:secondaryCols", 'git', 1, gitColDef, 10);
	reg.addToList("columns:netItems:secondaryCols", 'git', 1, gitColDef, 10);
	reg.addToList("columns:itemFoderTree:secondaryCols", 'git', 1, gitColDef, 10);

	reg.addToList("columns:wspApp:searches:item", "git", 1, gitColDef);


	/** */
	class GitHeadDiffAction extends DiffBarPanelAction {

		constructor(id?: string) {
			super(id || 'gitHead');
			this._label = "Git head";
		}

		async buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel> {
			return new (await import("/@back@/wsp/widgets/wed/wspGitDiffBar.js")).GitHeadDiffPanel().initPanel(this, ctx);
		}
	}

	reg.addToList("wed.diffBar.actions", "gitHead", 1, new GitHeadDiffAction());

	reg.addToList("wspApp:tools:rb", "gitChanges", 1, new NavigatorArea("gitChanges")
			.setLabel("Git").setDescription("Changements locaux Git")
			.setIcon("/@skin@/wsp/views/navigator/gitChanges.svg")
			.setEmptyTree((n) => <c-msg label="Aucune différence locale" level="info"/>)
			.setBuildFacets(async (nav, fields) => new NavigatorFacetGitChanges(fields))
			.setActionsLists(["actions:wspApp:navigator", "actions:wsp:shortDesc"])
			.setAccelKeysLists(["accelkeys:wspApp:navigator", "accelkeys:wsp:shortDesc"])
		, 10);


	const lstnFocusWin = () => {
		reg.env.universe.httpFrames.web.fetchSvc("u/srcGit", "RefreshWspIndexDiff", "param=" + encodeURIComponent(reg.env.wsp.code));
	}
	window.addEventListener("focus", lstnFocusWin);
	reg.onRegClose.add(() => {
		window.removeEventListener("focus", lstnFocusWin);
	});
}

export interface JGitHeadWedDiffBarParams extends JWedDiffBarParams {
}


export class NavigatorFacetGitChanges extends NavigatorFacetText {

	protected initSkin() {
		this.skin = ["wsp-navigator/facetText"]; //, "wsp-navigator/facetGitChanges"];
	}

	buildUiFacets(nav: Navigator, lastDatas: JLastDatas | null) {
		this.initRoot(nav).append(
			<div class="h">{this.buildTextInput(nav, lastDatas)}{nav.navigatorBtns}</div>
		);
	}

	protected getCriterions(): Element | Element[] | undefined {
		const exp = DOM.sharedXmlDoc().createElement("exp");
		exp.setAttribute("type", "GitState");
		exp.setAttribute("gitStates", "dirty");
		exp.setAttribute("deep", "true");
		exp.setAttribute("refresh", "true");
		return LANG.pushIfDefined([exp], this.getTextCriterion());
	}
}


//REG.reg.registerSkin('wsp-navigator/facetGitChanges', 1, /* language=CSS */ ``);